import { useMemo } from 'react';
import { DurationValue } from '../DurationSlider';

export interface BoostPricingInfo {
  freeBoostsRemaining: number;
  freeBoostsNeeded: number;
  pointsNeeded: number;
  canAfford: boolean;
  needsMorePoints: boolean;
  totalCost: string;
}

export interface UseBoostPricingParams {
  selectedDuration: DurationValue;
  freeBoostsRemaining: number;
  freeBoostsAllowed: number;
  pointsBalance: number;
}

export const useBoostPricing = ({
  selectedDuration,
  freeBoostsRemaining,
  freeBoostsAllowed,
  pointsBalance,
}: UseBoostPricingParams): BoostPricingInfo => {
  return useMemo(() => {
    // Use as many free boosts as possible for the duration
    // Each free boost covers 2 days (48 hours)
    // We can use up to the total free boosts allowed, but only if we have remaining ones
    const freeBoostsNeeded =
      freeBoostsRemaining > 0
        ? Math.min(
            Math.ceil(selectedDuration / 2), // How many free boosts needed for this duration
            freeBoostsAllowed // But not more than what's allowed
          )
        : 0; // No free boosts if none remaining

    // Calculate remaining duration that needs to be covered by points
    const remainingDuration = selectedDuration - freeBoostsNeeded * 2;
    const pointsNeeded = remainingDuration * 125; // 125 points per day (250 per 2 days)

    const canAfford = pointsBalance >= pointsNeeded;
    const needsMorePoints = pointsNeeded > 0 && !canAfford;

    const totalCost = `${
      freeBoostsNeeded > 0
        ? `${freeBoostsNeeded} free boost${freeBoostsNeeded > 1 ? 's' : ''}`
        : ''
    }${freeBoostsNeeded > 0 && pointsNeeded > 0 ? ' + ' : ''}${
      pointsNeeded > 0 ? `${pointsNeeded} points` : ''
    }`;

    return {
      freeBoostsRemaining,
      freeBoostsNeeded,
      pointsNeeded,
      canAfford,
      needsMorePoints,
      totalCost,
    };
  }, [selectedDuration, freeBoostsRemaining, freeBoostsAllowed, pointsBalance]);
};
